// /activate?token=…[&hint=…] — the user-facing landing the boat's
// first-run UI QR-encodes (Boat_Registration.md §2.1 step 2).
// Auth-gated: an unauthenticated visitor is bounced to /login with
// state.from set to the original URL so they return here after
// sign-in (or sign-up + auto-login).
//
// Behaviour: read ?token (and optional ?hint) from the query string,
// show a welcoming confirmation prompt naming the boat. Only on user
// confirm do we POST to /v1/boats with an Idempotency-Key derived from
// the token so a refresh after a successful claim is a cached replay.
// On 200, show a brief "your boat is connecting" acknowledgement with a
// clear path back to /dashboard (the boat's own first-run UI flips to
// "Linked" on its next poll) — not a silent instant redirect. On a known
// error code, render an inline message that names the cause; the boat's
// own polling continues independently.

import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { api } from '../api';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { safeReturnTo } from '../lib/returnTo';

type Status = 'awaiting_confirm' | 'submitting' | 'success' | 'error';

// activation_return_link: append ?ftue=1 to the boat origin so that, on return,
// Neomaris Core's boot() replays the magic splash (Linked → Scan → Devices) instead
// of dropping the user on the bare dashboard. `returnTo` is already guarded to
// a valid http(s) URL by safeReturnTo, so the parse won't throw.
function boatLinkWithFtue(returnTo: string): string {
  try {
    const u = new URL(returnTo);
    u.searchParams.set('ftue', '1');
    return u.toString();
  } catch {
    return returnTo;
  }
}

interface ApiError {
  status?: number;
  body?: { error?: string; message?: string };
  message?: string;
}

const ERROR_BANNERS: Record<string, string> = {
  invalid_input:
    'That activation token doesn’t look right. Check that you copied the whole code from your boat’s display.',
  activation_token_used:
    'That activation token has already been redeemed. If your boat is still in first-run mode, restart Neomaris Core to generate a fresh token.',
  activation_token_expired:
    'That activation token has expired. Restart Neomaris Core on your boat to generate a fresh one — they’re valid for 60 minutes.',
  activation_token_already_claimed:
    'That boat has already been claimed by a different account. Sign in with the original account, or contact the owner.',
};

export function Activate() {
  useDocumentTitle('Activate boat - Neomaris');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = (searchParams.get('token') ?? '').trim();
  const hint = (searchParams.get('hint') ?? '').trim();
  // activation_return_link: the boat's own origin, threaded through the auth
  // round-trip. Guarded to http(s) (display-only link, never navigated to
  // programmatically). When present, the success screen offers a one-tap
  // "Return to your boat" CTA.
  const boatHref = (() => {
    const rt = safeReturnTo(searchParams.get('return_to'));
    return rt !== null ? boatLinkWithFtue(rt) : null;
  })();

  const [status, setStatus] = useState<Status>('awaiting_confirm');
  const [name, setName] = useState(hint);
  const [error, setError] = useState<string | null>(null);
  // The display name we actually claimed the boat under — used to name
  // the boat warmly in the success acknowledgement.
  const [claimedName, setClaimedName] = useState<string>('');
  // The boatId returned by the claim (cloud_ftue_base 8.4) — used to land the
  // user on THIS boat's view (which reads "Connecting" until core checks in)
  // rather than the generic fleet list.
  const [claimedBoatId, setClaimedBoatId] = useState<string | null>(null);

  // If the URL has no token, surface that immediately — no confirm
  // step makes sense.
  useEffect(() => {
    if (token.length === 0) {
      setStatus('error');
      setError(
        'No activation token in the URL. Open the QR code or link from your boat’s first-run UI again.'
      );
    }
  }, [token]);

  // A friendly noun for the boat: the entered/hint name when present,
  // otherwise a gentle fallback so copy never reads "your ".
  const boatLabel = (claimedName || name.trim() || hint).trim();

  // cloud_ftue_base 8.4 → cloud-ftue Slice C: land a just-claimed boat on the
  // magical reveal (`/reveal?boatId=…`), which opens in a "connecting" grace
  // state and unveils the boat as collection + analysis land. Falls back to the
  // fleet list when the claim response carried no boatId.
  const dashboardHref = claimedBoatId !== null ? `/reveal?boatId=${claimedBoatId}` : '/dashboard';

  async function onConfirm(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus('submitting');
    setError(null);
    const trimmedName = name.trim();
    try {
      const res = await api<{ boatId?: string }>('/v1/boats', {
        method: 'POST',
        body: {
          activationToken: token,
          ...(trimmedName.length > 0 ? { name: trimmedName } : {}),
        },
        // Same key per token so a refresh after success is a no-op
        // (idempotency middleware replays the cached response).
        idempotencyKey: `activate-${token}`,
      });
      setClaimedName(trimmedName);
      setClaimedBoatId(typeof res?.boatId === 'string' ? res.boatId : null);
      setStatus('success');
    } catch (err) {
      const e = err as ApiError;
      const code = e.body?.error;
      const banner =
        (typeof code === 'string' ? ERROR_BANNERS[code] : undefined) ??
        e.body?.message ??
        'Something went wrong while confirming your boat. Try again, or contact support if it keeps happening.';
      setStatus('error');
      setError(banner);
    }
  }

  if (status === 'error') {
    return (
      <section className="auth-panel">
        <div className="auth-card">
          <h2>Activation didn&apos;t complete</h2>
          <ul className="form-errors" data-testid="error-banner">
            <li>{error}</li>
          </ul>
          <Link to="/dashboard" className="btn btn-primary auth-submit">
            Back to dashboard
          </Link>
        </div>
      </section>
    );
  }

  if (status === 'success') {
    return (
      <section className="auth-panel">
        <div className="auth-card" data-testid="activate-success">
          <h2>{boatLabel ? `${boatLabel} is connecting` : 'Your boat is connecting'}</h2>
          <p className="auth-sub">
            Nice work — {boatLabel ? `${boatLabel} is` : 'your boat is'} now linked to this account.
            {boatHref !== null
              ? ' Head back to it to finish setting up — it picks up right where you left off.'
              : ' Its screen flips to “Linked” on its next check-in and finishes setting itself up from there; it’ll show on your dashboard once it’s online.'}
          </p>
          {boatHref !== null ? (
            <>
              {/* Primary CTA: return to the boat (with ?ftue=1 so Neomaris Core
                  replays the magic Linked→Scan→Devices splash on arrival). */}
              <a
                href={boatHref}
                className="btn btn-primary auth-submit"
                data-testid="return-to-boat"
              >
                Return to your boat
              </a>
              <Link
                to={dashboardHref}
                className="auth-foot auth-cancel"
                data-testid="go-to-dashboard"
              >
                See your boat in the cloud instead
              </Link>
            </>
          ) : (
            <button
              type="button"
              className="btn btn-primary auth-submit"
              onClick={() => navigate(dashboardHref, { replace: true })}
              data-testid="go-to-dashboard"
            >
              See your boat
            </button>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="auth-panel">
      <div className="auth-card">
        <h2>{boatLabel ? `Welcome aboard ${boatLabel}` : 'Welcome aboard'}</h2>
        <p className="auth-sub">
          {boatLabel ? `${boatLabel} is` : 'Your boat is'} ready to link to this account. Confirm to
          connect it — your boat finishes the rest of setup on its own, then appears on your
          dashboard.
        </p>
        <form onSubmit={onConfirm} data-testid="activate-form">
          <div className="form-group">
            <label htmlFor="boat-name">Display name</label>
            <input
              type="text"
              id="boat-name"
              className="form-input"
              placeholder="New boat"
              value={name}
              onChange={(ev) => setName(ev.target.value)}
              maxLength={120}
            />
          </div>
          {/* The activation token is still read from the URL and sent on
              confirm, but we no longer surface it as prominent copyable
              text. It rides along as a hidden field so it's discoverable
              for support (e.g. via "view source" / DOM inspection) without
              competing with the welcoming copy. */}
          <input type="hidden" name="activation-token" value={token} readOnly />
          <button
            type="submit"
            className="btn btn-primary auth-submit"
            disabled={status === 'submitting'}
          >
            {status === 'submitting' ? 'Confirming…' : 'Register this boat'}
          </button>
          <Link to="/dashboard" className="auth-foot auth-cancel">
            Cancel
          </Link>
        </form>
      </div>
    </section>
  );
}
